import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Play, Square, Share2, Trash2, Radio } from "lucide-react";

export function LiveConsole({ tid, match, sport, onClose, onChanged }) {
  const [m, setM] = useState(match);
  const [presets, setPresets] = useState([]);
  const [player, setPlayer] = useState("");
  const [minute, setMinute] = useState("");
  const [motm, setMotm] = useState(match.motm_name || "");
  const [stream, setStream] = useState(match.stream_url || "");

  useEffect(() => {
    api.get(`/interclub/sports/${sport || "generic"}/events`).then((r) => setPresets(r.data.events)).catch(() => {});
  }, [sport]);

  const refresh = async () => {
    try { const r = await api.get(`/interclub/public/tournament/${tid}/match/${m.id}`); setM(r.data.match); onChanged && onChanged(); } catch (e) { /* noop */ }
  };
  const start = async () => { try { await api.post(`/interclub/tournaments/${tid}/matches/${m.id}/live/start`); await refresh(); toast.success("Match is live"); } catch (e) { toast.error(apiErr(e)); } };
  const end = async () => { try { await api.post(`/interclub/tournaments/${tid}/matches/${m.id}/live/end`, { motm_name: motm }); toast.success("Match ended · standings updated"); onChanged && onChanged(); onClose(); } catch (e) { toast.error(apiErr(e)); } };
  const saveStream = async () => { try { await api.put(`/interclub/tournaments/${tid}/matches/${m.id}/stream`, { url: stream }); setM((x) => ({ ...x, stream_url: stream })); toast.success("Stream link saved"); } catch (e) { toast.error(apiErr(e)); } };
  const addEvent = async (p, teamId) => {
    try {
      const r = await api.post(`/interclub/tournaments/${tid}/matches/${m.id}/live/event`, { type: p.type, label: p.label, points: p.points, team_id: teamId, player_name: player, minute: +minute || 0 });
      setM((x) => ({ ...x, score_a: r.data.score_a, score_b: r.data.score_b, events: [...(x.events || []), r.data.event] }));
      setPlayer("");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delEvent = async (eid) => { try { await api.delete(`/interclub/tournaments/${tid}/matches/${m.id}/live/event/${eid}`); await refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const liveUrl = `${window.location.origin}/tournament/${tid}/match/${m.id}`;
  const isLive = m.status === "live";
  const done = m.status === "completed";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="live-console">
        <DialogHeader><DialogTitle className="text-base">{m.team_a_name} vs {m.team_b_name}</DialogTitle></DialogHeader>
        <div className="text-center py-1">
          <p className="font-heading font-black text-4xl tabular-nums" data-testid="console-score">{m.score_a ?? 0} - {m.score_b ?? 0}</p>
          <p className="text-xs text-muted-foreground mt-1">{done ? "Full time" : isLive ? "● LIVE" : "Not started"}</p>
        </div>

        <div className="rounded-lg border border-border p-2">
          <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5"><Radio size={13} /> Live stream link (YouTube, TikTok, Facebook…)</p>
          <div className="flex gap-2">
            <Input placeholder="https://youtube.com/live/..." value={stream} onChange={(e) => setStream(e.target.value)} data-testid="stream-input" />
            <Button variant="outline" onClick={saveStream} data-testid="save-stream-btn">Save</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Fans on the public match page get a “Watch live stream” button.</p>
        </div>

        {!isLive && !done && <Button onClick={start} className="w-full brand-gradient text-white border-0" data-testid="start-live-btn"><Play size={16} className="mr-2" /> Go live</Button>}

        {isLive && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Player (optional)" value={player} onChange={(e) => setPlayer(e.target.value)} data-testid="event-player" />
              <Input type="number" placeholder="Minute" value={minute} onChange={(e) => setMinute(e.target.value)} data-testid="event-minute" />
            </div>
            {[["a", m.team_a_id, m.team_a_name], ["b", m.team_b_id, m.team_b_name]].map(([s, teamId, nm]) => (
              <div key={s} className="rounded-lg border border-border p-2">
                <p className="text-xs font-semibold mb-1.5">{nm}</p>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <Button key={p.type} size="sm" variant="outline" className="h-8" onClick={() => addEvent(p, teamId)} data-testid={`ev-${s}-${p.type}`}>{p.label}{p.points ? ` +${p.points}` : ""}</Button>
                  ))}
                </div>
              </div>
            ))}
            <Input placeholder="Player of the Match (optional)" value={motm} onChange={(e) => setMotm(e.target.value)} data-testid="motm-input" />
            <Button onClick={end} variant="outline" className="w-full" data-testid="end-live-btn"><Square size={16} className="mr-2" /> End match</Button>
          </>
        )}

        {(m.events || []).length > 0 && (
          <div className="space-y-1 mt-1">
            {[...m.events].sort((a, b) => (b.minute || 0) - (a.minute || 0)).map((ev) => (
              <div key={ev.id} className="flex items-center justify-between text-sm border-b border-border/60 py-1">
                <span>{ev.minute ? `${ev.minute}' ` : ""}{ev.label}{ev.player_name ? ` · ${ev.player_name}` : ""} <span className="text-muted-foreground">({ev.team_id === m.team_a_id ? m.team_a_name : m.team_b_name})</span></span>
                <button onClick={() => delEvent(ev.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}

        <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(liveUrl); toast.success("Public live link copied"); }} data-testid="copy-live-link"><Share2 size={14} className="mr-1" /> Copy public live link</Button>
      </DialogContent>
    </Dialog>
  );
}
